import {
  CarOutlined,
  MailOutlined,
  MessageOutlined,
  ToolOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import type { ReactNode } from "react";

export type MessageChannel = "platform" | "email" | "whatsapp";

export interface ConversationMessage {
  id: string;
  sender: string;
  content: string;
  timestamp: string;
  channel: MessageChannel;
  isOutgoing: boolean;
}

export interface StakeholderConversation {
  key: string;
  name: string;
  role: string;
  icon: ReactNode;
  unreadCount: number;
  messages: ConversationMessage[];
}

const now = Date.now();
const hours = (h: number) => h * 60 * 60 * 1000;
const days = (d: number) => d * 24 * 60 * 60 * 1000;

export const channelConfig: Record<
  MessageChannel,
  { label: string; icon: ReactNode; color: string }
> = {
  platform: {
    label: "Platform",
    icon: <MessageOutlined style={{ fontSize: 12 }} />,
    color: "blue",
  },
  email: {
    label: "E-mail",
    icon: <MailOutlined style={{ fontSize: 12 }} />,
    color: "orange",
  },
  whatsapp: {
    label: "WhatsApp",
    icon: <WhatsAppOutlined style={{ fontSize: 12 }} />,
    color: "green",
  },
};

export const buildMockConversations = (
  driverName: string,
  servicePartnerName: string,
): StakeholderConversation[] => {
  const driverFirstName = driverName.split(" ")[0];

  return [
    {
      key: "driver",
      name: driverName,
      role: "Bestuurder",
      icon: <CarOutlined />,
      unreadCount: 2,
      messages: [
        {
          id: "d1",
          sender: "Systeem",
          content: `Beste ${driverFirstName}, naar aanleiding van je schademelding vragen we je om foto's van de schade aan te leveren. Je kunt deze uploaden via het portaal of als bijlage sturen.`,
          timestamp: new Date(now - days(7)).toISOString(),
          channel: "whatsapp",
          isOutgoing: true,
        },
        {
          id: "d2",
          sender: driverName,
          content:
            "Goedemiddag, ik heb het bericht ontvangen. Ik zal vanavond de foto's maken en opsturen.",
          timestamp: new Date(now - days(7) + hours(4)).toISOString(),
          channel: "whatsapp",
          isOutgoing: false,
        },
        {
          id: "d3",
          sender: "Systeem",
          content:
            "Herinnering: we hebben nog geen foto's ontvangen. Kun je deze zo snel mogelijk aanleveren?",
          timestamp: new Date(now - days(4)).toISOString(),
          channel: "whatsapp",
          isOutgoing: true,
        },
        {
          id: "d4",
          sender: driverName,
          content: "Sorry, was druk. Hier zijn de foto's van de schade",
          timestamp: new Date(now - days(3)).toISOString(),
          channel: "whatsapp",
          isOutgoing: false,
        },
        {
          id: "d5",
          sender: "Systeem",
          content: `Bedankt ${driverFirstName}! De foto's zijn ontvangen. We nemen de schade in behandeling en je hoort zo snel mogelijk van ons.`,
          timestamp: new Date(now - days(3) + hours(1)).toISOString(),
          channel: "platform",
          isOutgoing: true,
        },
        {
          id: "d6",
          sender: driverName,
          content: "Hoe lang duurt de reparatie ongeveer?",
          timestamp: new Date(now - days(1)).toISOString(),
          channel: "whatsapp",
          isOutgoing: false,
        },
        {
          id: "d7",
          sender: driverName,
          content: "En is er vervangend vervoer beschikbaar?",
          timestamp: new Date(now - days(1) + hours(0.5)).toISOString(),
          channel: "whatsapp",
          isOutgoing: false,
        },
      ],
    },
    {
      key: "claims-handler",
      name: "Lisa van Dijk",
      role: "Claim Afhandelaar",
      icon: <MessageOutlined />,
      unreadCount: 0,
      messages: [
        {
          id: "c1",
          sender: "Robert Guliker",
          content:
            "Lisa, kun je de verhaalbaarheid voor dit dossier beoordelen? Het betreft een aanrijding met een bekende tegenpartij.",
          timestamp: new Date(now - days(6)).toISOString(),
          channel: "platform",
          isOutgoing: true,
        },
        {
          id: "c2",
          sender: "Lisa van Dijk",
          content:
            "Ik heb het schadeformulier bekeken. De tegenpartij heeft schuld erkend en er is een getuigenverklaring. Ik denk dat dit verhaalbaar is.",
          timestamp: new Date(now - days(6) + hours(2)).toISOString(),
          channel: "platform",
          isOutgoing: false,
        },
        {
          id: "c3",
          sender: "Robert Guliker",
          content:
            "Top, bedankt. Heb je al contact gehad met de verzekeraar van de tegenpartij?",
          timestamp: new Date(now - days(3) + hours(2)).toISOString(),
          channel: "platform",
          isOutgoing: true,
        },
        {
          id: "c4",
          sender: "Lisa van Dijk",
          content:
            "Ja, de aansprakelijkheid is bevestigd door de verzekeraar van de tegenpartij. We kunnen de schade volledig verhalen. Ik heb de verhaaldocumenten opgesteld.",
          timestamp: new Date(now - days(3) + hours(3)).toISOString(),
          channel: "platform",
          isOutgoing: false,
        },
        {
          id: "c5",
          sender: "Lisa van Dijk",
          content:
            "De verhaalvordering is ingediend bij de verzekeraar van de tegenpartij. Verwachte afhandeling binnen 4 weken.",
          timestamp: new Date(now - days(1) + hours(5)).toISOString(),
          channel: "platform",
          isOutgoing: false,
        },
      ],
    },
    {
      key: "repair-shop",
      name: servicePartnerName,
      role: "Hersteller",
      icon: <ToolOutlined />,
      unreadCount: 1,
      messages: [
        {
          id: "r1",
          sender: "Robert Guliker",
          content: `Goedemorgen, Kunnen jullie een afspraak inplannen voor dit schadedossier?`,
          timestamp: new Date(now - days(2)).toISOString(),
          channel: "platform",
          isOutgoing: true,
        },
        {
          id: "r2",
          sender: servicePartnerName,
          content:
            "Goedemorgen Robert. We hebben plek op donderdag 24 april om 09:00. Past dat voor de bestuurder?",
          timestamp: new Date(now - days(2) + hours(3)).toISOString(),
          channel: "platform",
          isOutgoing: false,
        },
        {
          id: "r3",
          sender: "Robert Guliker",
          content: `Dat past prima. De bestuurder is ${driverName}. Verwachte reparatieduur?`,
          timestamp: new Date(now - days(2) + hours(4)).toISOString(),
          channel: "platform",
          isOutgoing: true,
        },
        {
          id: "r4",
          sender: servicePartnerName,
          content:
            "Op basis van de calculatie schatten we 3 werkdagen. We houden je op de hoogte van de voortgang.",
          timestamp: new Date(now - days(1) + hours(2)).toISOString(),
          channel: "platform",
          isOutgoing: false,
        },
        {
          id: "r5",
          sender: servicePartnerName,
          content:
            "Update: onderdelen zijn besteld. Levering verwacht woensdag, daarna starten we direct met de reparatie.",
          timestamp: new Date(now - hours(3)).toISOString(),
          channel: "platform",
          isOutgoing: false,
        },
      ],
    },
  ];
};
